import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Button, Checkbox, HStack, Input, InputGroup, Spinner, Stack, Text, VStack } from "@chakra-ui/react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createClickLog, getMeilisearchAutoComplete, searchMedicines } from "../../api";
import LogInOnly from "../../components/Auth/LogInOnly";
import Template from "../../components/Layout/Template";
import MedicineGroupListItem from "../../components/Medicine/MedicineGroupListItem";
import { colors } from "../../styles";
import { IMedicineGroup, IMedicineGroupList, IMedicineGroupListByIngredientCode, IMeilisearchAutoComplete } from "../../types";
import { useAuthStore } from "../../store";
import useMe from "../../hooks/useMe";
import MyIcon from "../../components/Icon/MyIcon";
import Alert, { useAlert } from "../../components/Alert/Alert";
import AdListItem from "../../components/Ad/AdListItem";

export default function Search() {

    /* useMe */

    const { meData } = useMe();

    const userType = useMemo(() => meData?.user.type ?? "", [meData]);
    const userStatus = useMemo(() => meData?.user.status ?? "", [meData]);

    const isOwnerAndActive = useMemo(() => userType === "OWNER" && userStatus === "ACTIVE", [userType, userStatus]);

    /* useAuthStore */

    const { isGuest } = useAuthStore();

    const isRestrictedUser = useMemo(() => isGuest || userType === "MEMBER", [isGuest, userType]);
    const restrictedAlertMessage = useMemo(
        () => (userType === "MEMBER" ? "승인 후 이용할 수 있습니다" : "회원가입 후 이용할 수 있습니다."),
        [userType]
    );
    const restrictedFilterMessage = useMemo(
        () =>
            userType === "MEMBER"
                ? "승인 후 포장 타입 및 단위를 필터링할 수 있습니다."
                : "회원 가입 후 포장 타입 및 단위를 필터링할 수 있습니다.",
        [userType]
    );

    const { showAlert, alertProps } = useAlert();

    /* search params */

    const [searchParams, setSearchParams] = useSearchParams();

    /* 검색어 */

    const [searchInput, setSearchInput] = useState<string>("");
    const [searchKeyword, setSearchKeyword] = useState<string>("");

    const [debouncedSearchInput, setDebouncedSearchInput] = useState<string>("");
    const [isSearchInputFocused, setIsSearchInputFocused] = useState<boolean>(false);

    const [packageType, setPackageType] = useState<string | null>(null);
    const [packageUnit, setPackageUnit] = useState<string | null>(null);

    /** 필터 없이 로드된 전체 availableFilters (packageType 선택 후에도 목록 유지, 검색어 변경 시 비움) */

    const [fullAvailableFiltersForUi, setFullAvailableFiltersForUi] = useState<Record<string, string[]>>({});

    /* 같은 성분 묶어보기 체크 */

    const [isSameIngredientCodeGrouping, setIsSameIngredientCodeGrouping] = useState<boolean>(false);

    /* 검색어 변경 시 필터·필터 목록 초기화 */

    useEffect(() => {
        setPackageType(null);
        setPackageUnit(null);
        setIsSameIngredientCodeGrouping(false);
        setFullAvailableFiltersForUi({});
    }, [searchKeyword]);

    const searchBoxRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    /* useInfiniteQuery */

    const size = 20;

    const { 
        data: medicineGroupListData, 
        isLoading: isMedicineGroupListLoading, 
        isFetching: isMedicineGroupListFetching,
        fetchNextPage: fetchNextMedicineGroupList, 
        hasNextPage: hasNextMedicineGroupListPage, 
        isFetchingNextPage: isFetchingNextMedicineGroupListPage, 
    } = useInfiniteQuery<IMedicineGroupList | IMedicineGroupListByIngredientCode>({

        queryKey: ['searchMedicines', searchKeyword, packageType, packageUnit, isSameIngredientCodeGrouping],

        queryFn: ({ pageParam }) => {

            const page = pageParam as number;
            
            return searchMedicines({ 
                page, 
                size, 
                searchKeyword, 
                packageType,
                packageUnit,
                isSameIngredientCodeGrouping,
            });
        },

        initialPageParam: 1,

        enabled: !!searchKeyword,

        getNextPageParam: (lastPageData: IMedicineGroupList | IMedicineGroupListByIngredientCode) => {
            return lastPageData.hasNextPage ? lastPageData.currentPage + 1 : undefined;
        },

        staleTime: Infinity,
        gcTime: 1000 * 60 * 60 * 24, 
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
    });

    /* 필터 */

    const availableFilters = useMemo(() => medicineGroupListData?.pages[0]?.availableFilters ?? {}, [medicineGroupListData]);

    /* 필터 없이 로드된 응답이 올 때마다 UI용 전체 목록 갱신 (검색 요청이 끝난 뒤에만 설정해 이전 검색 결과로 채우지 않음) */

    useEffect(() => {

        const isUiFiltersEmpty = (packageType === null && packageUnit === null);
        const dataHasAvailableFilters = (Object.keys(availableFilters).length > 0);

        if (!isMedicineGroupListFetching && isUiFiltersEmpty && dataHasAvailableFilters) {
            setFullAvailableFiltersForUi(availableFilters);
        }

    }, [isMedicineGroupListFetching, availableFilters, packageType, packageUnit]);

    /* availableFilters: { [packageType]: packageUnit[] } → 포장 타입 목록 (항상 전체 표시, 정렬로 순서 고정, 공백 값은 제외) */

    const packageTypeList = useMemo(
        () => Object.keys(fullAvailableFiltersForUi)
            .filter((key) => key.trim() !== "")
            .sort((a, b) => a.localeCompare(b)),
        [fullAvailableFiltersForUi]
    );

    /* 선택한 packageType에 따른 포장 단위 목록 (packageType 미선택 시 빈 배열, 선택 시에만 표시, 정렬로 순서 고정, 공백 값은 제외) */

    const packageUnitList = useMemo(() => {
        const raw = packageType ? (fullAvailableFiltersForUi[packageType] ?? []) : [];
        return raw
            .filter((unit) => unit.trim() !== "")
            .sort((a, b) => a.localeCompare(b));
    }, [fullAvailableFiltersForUi, packageType]);

    /* 포장 타입 변경 */

    const handleChangePackageType = (type: string | null) => {
        if (type === packageType) {
            setPackageType(null);
            setPackageUnit(null);
        } else {
            setPackageType(type);
            setPackageUnit(null);
        }
    };

    /* 포장 단위 변경 */

    const handleChangePackageUnit = (unit: string | null) => {
        if (unit === packageUnit) {
            setPackageUnit(null);
        } else {
            setPackageUnit(unit);
        }
    };

    /* 검색 결과 렌더링 함수 */

    type DisplayItem =
        | { type: "ad"; slotIndex: number }
        | { type: "group"; group: IMedicineGroup; pageIdx: number; idxInPage: number;};

    type AccordionBlock =
        | { type: "ad"; slotIndex: number }
        | { type: "section"; labelText: string; groups: IMedicineGroup[]; sectionKey: string };

    const renderSearchResult = () => {

        const pages = medicineGroupListData?.pages ?? [];

        ////////////////////////////// 같은 성분 묶어보기 ON: 성분별 Accordion 블록 구성 (광고는 6그룹마다)

        if (isSameIngredientCodeGrouping) {

            const accordionBlocks: AccordionBlock[] = [];
            let accordionCountSinceLastAd = 0;
            let adSlotIndex = 0;

            pages.forEach((page, pageIdx) => {

                const byIngredientPage = page as IMedicineGroupListByIngredientCode;

                byIngredientPage.groups.forEach((topLevel, topIdx) => {

                    if (topLevel.groups.length === 0) return;

                    const labelText = (topLevel.ingredientInfo) || "(성분 정보 없음)";

                    if (accordionCountSinceLastAd > 0 && accordionCountSinceLastAd % 6 === 0) {
                        accordionBlocks.push({ type: "ad", slotIndex: adSlotIndex });
                        adSlotIndex += 1;
                    }

                    accordionBlocks.push({
                        type: "section",
                        labelText,
                        groups: topLevel.groups,
                        sectionKey: `acc-${pageIdx}-${topIdx}-${topLevel.ingredientCode ?? topIdx}`,
                    });
                    accordionCountSinceLastAd += 1;
                });
            });

            return (
                <Stack width="100%" spacing={0}>
                    {accordionBlocks.map((block) => {
                        if (block.type === "ad") {
                            return <AdListItem
                                key={`ad-${block.slotIndex}`}
                                slotIndex={block.slotIndex}
                            />;
                        }
                        return (
                            <Accordion key={block.sectionKey} allowMultiple marginY={2}>
                                <AccordionItem paddingX={0}>
                                    <AccordionButton
                                        paddingX={3}
                                        paddingY={3}
                                        _hover={{ backgroundColor: colors.light }}
                                        _expanded={{ backgroundColor: colors.light }}
                                    >
                                        <AccordionIcon />
                                        <HStack flex={1} textAlign="left" marginLeft={3} gap={2} flexWrap="wrap">
                                            <Text fontSize={14} fontWeight={500} color={colors.black}>
                                                {block.labelText}
                                            </Text>
                                            <Text fontSize={12} fontWeight={400} color={colors.g2}>
                                                ({block.groups.length}개)
                                            </Text>
                                        </HStack>
                                    </AccordionButton>
                                    <AccordionPanel paddingX={0} paddingBottom={4}>
                                        <Stack spacing={5} marginTop={2}>
                                            {block.groups.map((group) => (
                                                <MedicineGroupListItem
                                                    key={group.seqNo}
                                                    seqNo={group.seqNo}
                                                    medicineList={group.medicineList}
                                                    searchKeyword={searchKeyword}
                                                    isGuest={isRestrictedUser}
                                                    restrictedAlertMessage={restrictedAlertMessage}
                                                    isOwnerAndActive={isOwnerAndActive}
                                                />
                                            ))}
                                        </Stack>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>
                        );
                    })}
                </Stack>
            );
        }

        ////////////////////////////// 같은 성분 묶어보기 OFF: 플랫 리스트
        // GUEST: 처음 2개 뒤 광고, 이후 3개마다 광고
        // 그 외: 페이지 안에서 처음 2개 뒤 광고, 이후 8개마다 광고

        const items: DisplayItem[] = [];
        const isGuestUser = userType === "GUEST";
        let medicinesSinceLastAd = 0;
        let hasInsertedAd = false;
        let adSlotIndex = 0;

        pages.forEach((page, pageIdx) => {
            const flatPage = page as IMedicineGroupList;
            flatPage.groups.forEach((group, idxInPage) => {
                if (isGuestUser) {
                    const interval = hasInsertedAd ? 3 : 2;
                    if (medicinesSinceLastAd === interval) {
                        items.push({ type: "ad", slotIndex: adSlotIndex });
                        adSlotIndex += 1;
                        medicinesSinceLastAd = 0;
                        hasInsertedAd = true;
                    }
                } else if (idxInPage >= 2 && (idxInPage - 2) % 8 === 0) {
                    items.push({ type: "ad", slotIndex: adSlotIndex });
                    adSlotIndex += 1;
                }
                items.push({
                    type: "group",
                    group,
                    pageIdx,
                    idxInPage,
                });
                if (isGuestUser) medicinesSinceLastAd += 1;
            });
        });

        return items.map((item) => {
            if (item.type === "ad") {
                return <AdListItem
                            key={`ad-${item.slotIndex}`}
                            slotIndex={item.slotIndex}
                        />;
            }
            const { group } = item;
            return (
                <MedicineGroupListItem
                    key={`${group.seqNo}-${item.pageIdx}-${item.idxInPage}`}
                    seqNo={group.seqNo}
                    medicineList={group.medicineList}
                    searchKeyword={searchKeyword}
                    isGuest={isRestrictedUser}
                    restrictedAlertMessage={restrictedAlertMessage}
                    isOwnerAndActive={isOwnerAndActive}
                />
            );
        });
    }

    /* useEffect - URL 쿼리(q) → 입력/검색값 동기화. type 은 무시한다. */

    useEffect(() => {

        const q = searchParams.get("q") ?? "";

        setSearchInput(q);
        setSearchKeyword(q);

    }, [searchParams]);

    /* 입력값 업데이트 */

    const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {

        const nextInputValue = event.target.value ?? "";
        setSearchInput(nextInputValue);
    };

    /* 검색 실행 */

    const submitSearch = (rawKeyword: string) => {
    
        const nextKeyword = rawKeyword.trim();

        setSearchKeyword(nextKeyword);
    
        const nextParams = new URLSearchParams(searchParams);
    
        if (nextKeyword === "") {
            nextParams.delete("q");
        } else {
            nextParams.set("q", nextKeyword);
        }

        nextParams.delete("type");
    
        setSearchParams(nextParams);
    };
    
    /* Enter 검색 */
    
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    
        if (event.key !== "Enter") return;
    
        if ((event.nativeEvent as any).isComposing) return;
        
        const keyword = event.currentTarget.value.trim();
        
        if (!keyword) return;
    
        setSearchInput(keyword);
        submitSearch(keyword);
    };

    /* 자동 완성: 바깥 클릭 시 닫기 */

    useEffect(() => {

        const handleClickOutside = (event: MouseEvent) => {

            if (!searchBoxRef.current) return;

            if (!searchBoxRef.current.contains(event.target as Node)) {

                setIsSearchInputFocused(false);
                inputRef.current?.blur();
            }
        };

        if (isSearchInputFocused) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        }

    }, [isSearchInputFocused]);

    /* input 디바운스 */

    useEffect(() => {

        const handler = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
        }, 300);

        return () => {
            clearTimeout(handler);
        };

    }, [searchInput]);

    /* meilisearch 자동 완성 */

    const trimmedDebouncedSearchInput = debouncedSearchInput.trim();

    const { data: meilisearchAutoCompleteData } = useQuery<IMeilisearchAutoComplete>({
        queryKey: ['getMeilisearchAutoComplete', trimmedDebouncedSearchInput],
        queryFn: () => getMeilisearchAutoComplete({ searchKeyword: trimmedDebouncedSearchInput }),
        enabled: trimmedDebouncedSearchInput.length > 1
    });

    type MeilisearchAutoCompleteItem = {
        type: string;
        value: string;
    }

    const autoCompleteArray: MeilisearchAutoCompleteItem[] = [
        ...(meilisearchAutoCompleteData?.products ?? []).map((item) => {return {type: item.type, value: item.value}}),
        ...(meilisearchAutoCompleteData?.keywords ?? []).map((item) => {return {type: item.type, value: item.value}}),
    ];

    /* 자동 완성: 필터링된 리스트 */

    const filteredAutoCompleteArray = autoCompleteArray.filter((item) =>
        item.value.toLowerCase().includes(searchInput.toLowerCase())
    );

    const shouldShowAutoComplete =
        isSearchInputFocused &&
        searchInput.trim().length > 1 &&
        filteredAutoCompleteArray.length > 0;

    const handleSelectAutoComplete = (keyword: string) => {

        const trimmed = keyword.trim();
        if (!trimmed) return;

        setSearchInput(trimmed);
        submitSearch(trimmed);

        setIsSearchInputFocused(false);
        inputRef.current?.blur();
    };

    /* render */

    return (
        <LogInOnly>
            <Template>

                {/* 검색창 */}

                <Box
                    ref={searchBoxRef}
                    width={"100%"}
                    display="flex"
                    justifyContent="center"
                    position="relative"
                    zIndex={10}
                    paddingX={{
                        base: 0,
                        sm: 0,
                        md: 5,
                        lg: 5,
                        xl: 5,
                    }}
                    paddingY={5}
                >
                    <Box
                        width={"100%"}
                    >
                        <InputGroup
                            margin="0 auto"
                            padding="0"
                            backgroundColor={colors.blue}
                            borderRadius={24}
                            boxShadow="md"
                        >
                            <Input
                                ref={inputRef}
                                value={searchInput}
                                onChange={handleChangeInput}
                                onKeyDown={handleKeyDown}
                                placeholder="💊  약품에 대해 단어로 검색해 보세요!"
                                _placeholder={{ fontSize: 16, color: colors.white, opacity: 0.7 }}
                                backgroundColor="transparent"
                                border="none"
                                borderRadius={24}
                                fontSize={18}
                                color={colors.white}
                                _focus={{
                                    boxShadow: "none",
                                    border: "none",
                                }}
                                height={14}
                                paddingX={5}
                                onFocus={() => setIsSearchInputFocused(true)}
                            />
                        </InputGroup>

                        {shouldShowAutoComplete && (
                            <VStack
                                alignItems={"flex-start"}
                                spacing={0}
                                position="absolute"
                                top="100%"
                                left="50%"
                                transform="translateX(-50%)"
                                width={"96%"}
                                marginTop={3}
                                backgroundColor={"white"}
                                borderRadius={16}
                                boxShadow="md"
                                overflow="hidden"
                            >
                                {filteredAutoCompleteArray.map((item, idx) => (
                                    <HStack
                                        key={`${item}-${idx}`}
                                        as="button"
                                        type="button"
                                        width="100%"
                                        paddingX={4}
                                        paddingY={3}
                                        spacing={2}
                                        justifyContent={"flex-start"}
                                        _hover={{ backgroundColor: colors.light }}
                                        onClick={() => handleSelectAutoComplete(item.value)}
                                    >
                                        {item.type === "product" && <MyIcon src="/svg/capsule-filled.svg" alt="medicine" width={20} height={20} />}
                                        {item.type === "keyword" && <MyIcon src="/svg/search.svg" alt="search" width={20} height={20} />}
                                        <Text fontSize={16} color={colors.black}>
                                            {item.value}
                                        </Text>
                                    </HStack>
                                ))}
                            </VStack>
                        )}
                    </Box>
                </Box>

                {/* 안내 문구 */}

                <VStack 
                    justifyContent={"center"} 
                    alignItems={"flex-start"}                     
                    paddingX={{
                        base: 0,
                        sm: 0,
                        md: 5,
                        lg: 5,
                        xl: 5,
                    }}
                    paddingBottom={10}

                    marginTop={-3}
                >
                    <Text fontSize={14} fontWeight={400} color={colors.g3}>예) 타이레놀, 소화제</Text>
                </VStack>

                <Stack width={"100%"} marginBottom={10}>

                    {/* 포장 타입 (검색 결과 로드 시 표시, 단일 선택, 토글 시 해제) */}

                    {!isRestrictedUser && packageTypeList.length > 0 && (
                        <Stack
                            width={"100%"}
                            animation="fadeInUp 0.6s ease"
                            sx={{
                                '@keyframes fadeInUp': {
                                    from: {
                                        opacity: 0,
                                        transform: 'translate3d(0, 30px, 0)'
                                    },
                                    to: {
                                        opacity: 1,
                                        transform: 'none'
                                    }
                                }
                            }}
                        >
                            <Text fontSize={14} fontWeight={400} color={colors.black}>포장 타입을 선택할 수 있습니다.</Text>
                            <HStack width={"100%"} justifyContent={"flex-start"} marginTop={2} marginBottom={10} spacing={2} flexWrap={"wrap"}>
                                {packageTypeList.map((type) => (
                                    <Button
                                        key={type}
                                        onClick={() => handleChangePackageType(type === packageType ? null : type)}
                                        variant={"ghost"}
                                        size={"md"}
                                        backgroundColor={type === packageType ? colors.blue : colors.g5}
                                        color={type === packageType ? colors.white : colors.black}
                                        _hover={{
                                            backgroundColor: type === packageType ? colors.blue : colors.g5,
                                            color: type === packageType ? colors.white : colors.black,
                                        }}
                                        fontWeight={type === packageType ? 700 : 300}
                                        fontSize={type === packageType ? 16 : 14}
                                    >
                                        {type}
                                    </Button>
                                ))}
                            </HStack>

                            {/* 포장 단위 (packageType 선택 시에만 표시) */}

                            {packageType && (
                                <Stack
                                    width={"100%"}
                                    animation="fadeInUp 0.6s ease"
                                    sx={{
                                        '@keyframes fadeInUp': {
                                            from: {
                                                opacity: 0,
                                                transform: 'translate3d(0, 30px, 0)'
                                            },
                                            to: {
                                                opacity: 1,
                                                transform: 'none'
                                            }
                                        }
                                    }}
                                >
                                    <Text fontSize={14} fontWeight={400} color={colors.black}>포장 단위를 선택할 수 있습니다.</Text>
                                    <HStack width={"100%"} justifyContent={"flex-start"} marginTop={2} spacing={2} flexWrap={"wrap"}>
                                        {packageUnitList.map((unit) => (
                                            <Button
                                                key={unit}
                                                onClick={() => handleChangePackageUnit(unit === packageUnit ? null : unit)}
                                                variant={"ghost"}
                                                size={"md"}
                                                backgroundColor={unit === packageUnit ? colors.blue : colors.g5}
                                                color={unit === packageUnit ? colors.white : colors.black}
                                                _hover={{
                                                    backgroundColor: unit === packageUnit ? colors.blue : colors.g5,
                                                    color: unit === packageUnit ? colors.white : colors.black,
                                                }}
                                                fontWeight={unit === packageUnit ? 700 : 300}
                                                fontSize={unit === packageUnit ? 16 : 14}
                                            >
                                                {unit}
                                            </Button>
                                        ))}
                                    </HStack>
                                </Stack>
                            )}
                            <Stack width={"100%"} height={10} marginTop={2} />
                        </Stack>
                    )}

                    {isRestrictedUser && packageTypeList.length > 0 && (
                        <Box 
                            width={"100%"} 
                            padding={4} 
                            backgroundColor={colors.g5} 
                            borderRadius={8}
                        >
                            <Text fontSize={14} fontWeight={400} color={colors.black}>{restrictedFilterMessage}</Text>
                        </Box>
                    )}
                </Stack>

                {/* 검색 결과 수 */}

                <Stack 
                    width={"100%"}
                    paddingX={{
                        base: 0,
                        sm: 0,
                        md: 5,
                        lg: 5,
                        xl: 5,
                    }}
                    paddingY={5}
                >
                    {medicineGroupListData && (
                        <HStack 
                            width={"100%"} 
                            justifyContent={"space-between"} 
                            alignItems={"center"}
                        >
                            <Text fontSize={14} fontWeight={400} color={colors.black}>검색 결과 {medicineGroupListData.pages[0].totalCount ?? 0}건</Text>
                            <HStack justifyContent={"flex-start"} alignItems={"center"} gap={2}>
                                <Checkbox 
                                    isChecked={isSameIngredientCodeGrouping} 
                                    onChange={() => {
                                        if (isRestrictedUser) {
                                            showAlert(restrictedAlertMessage);
                                            return;
                                        };
                                        // 체크를 켤 때만 기록한다. 검색어가 바뀌며 풀리는 경우는 클릭이 아니다.
                                        if (!isSameIngredientCodeGrouping && searchKeyword) {
                                            createClickLog({
                                                type: "INGREDIENT_GROUP",
                                                searchKeyword,
                                            });
                                        }
                                        setIsSameIngredientCodeGrouping(!isSameIngredientCodeGrouping);
                                    }} 
                                    colorScheme="blue"
                                />
                                <Text fontSize={14} fontWeight={400} color={colors.black}>같은 성분 묶어보기</Text>
                            </HStack>
                        </HStack>
                    )}
                </Stack>

                {/* 검색 결과 */}

                <Stack 
                    width={"100%"}
                    spacing={5}
                    paddingBottom={5}
                >
                    {renderSearchResult()}

                    {isMedicineGroupListLoading && 
                        <Stack width={"100%"} justifyContent={"center"} alignItems={"center"}>
                            <Spinner color={colors.blue} />
                        </Stack>
                    }

                    {hasNextMedicineGroupListPage && (
                        <Button variant={"ghost"} onClick={() => fetchNextMedicineGroupList()} isLoading={isFetchingNextMedicineGroupListPage}>
                            더보기
                        </Button>
                    )}
                </Stack>
            </Template>

            <Alert {...alertProps} />
        </LogInOnly>
    );
}
